import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import FamilyRestroomRoundedIcon from "@mui/icons-material/FamilyRestroomRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import { useNavigate } from "react-router-dom";

import LoggedInLayout from "../../layout/LoggedInLayout";
import ParentFeedback from "../Assessment/ParentFeedback";
import ParentFeedbackHistory from "./ParentFeedbackHistory";
import {
  fetchParentFeedbackHistory,
  fetchParentFeedbackStatus,
  fetchParentStudentLinks,
} from "../../services/api";

const SELECTED_PARENT_CHILD_STORAGE_KEY = "parent-portal-selected-child";

const cardSx = {
  width: "100%",
  maxWidth: 900,
  backgroundColor: "#FFFFFF",
  borderRadius: "14px",
  p: { xs: 2, sm: 3 },
  boxShadow: "0 18px 45px rgba(41, 45, 91, 0.08)",
  border: "1px solid rgba(41, 45, 91, 0.08)",
};

const ParentAssessmentPage = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState("");
  const [linksLoading, setLinksLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState(null);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const feedbackLoadId = useRef(0);

  const selectedChild = useMemo(
    () => children.find((child) => String(child.id) === String(selectedChildId)),
    [children, selectedChildId],
  );

  const loadChildren = useCallback(async () => {
    try {
      setLinksLoading(true);
      const response = await fetchParentStudentLinks();
      const links = response?.data?.links || response?.data?.data?.links || [];
      const acceptedChildren = links
        .filter((link) => link.status === "accepted" && link.student)
        .map((link) => link.student)
        .filter(
          (child, index, list) =>
            child?.id && list.findIndex((item) => item.id === child.id) === index,
        );

      setChildren(acceptedChildren);

      const storedChildId = localStorage.getItem(SELECTED_PARENT_CHILD_STORAGE_KEY);
      const nextSelected =
        acceptedChildren.find((child) => String(child.id) === storedChildId)?.id ||
        acceptedChildren[0]?.id ||
        "";

      setSelectedChildId(nextSelected ? String(nextSelected) : "");
      if (nextSelected) {
        localStorage.setItem(SELECTED_PARENT_CHILD_STORAGE_KEY, String(nextSelected));
      } else {
        localStorage.removeItem(SELECTED_PARENT_CHILD_STORAGE_KEY);
      }
    } catch {
      setChildren([]);
      setSelectedChildId("");
      localStorage.removeItem(SELECTED_PARENT_CHILD_STORAGE_KEY);
    } finally {
      setLinksLoading(false);
    }
  }, []);

  const loadFeedbackData = useCallback(async (studentUserId) => {
    const requestId = feedbackLoadId.current + 1;
    feedbackLoadId.current = requestId;

    if (!studentUserId) {
      setFeedbackStatus(null);
      setFeedbackHistory([]);
      return;
    }

    setFeedbackStatus(null);
    setFeedbackHistory([]);
    setStatusLoading(true);
    setHistoryLoading(true);

    const [statusResult, historyResult] = await Promise.allSettled([
      fetchParentFeedbackStatus(studentUserId),
      fetchParentFeedbackHistory(studentUserId),
    ]);

    if (feedbackLoadId.current !== requestId) return;

    setFeedbackStatus(
      statusResult.status === "fulfilled" ? statusResult.value?.data || null : null,
    );
    setFeedbackHistory(
      historyResult.status === "fulfilled"
        ? historyResult.value?.data?.history || []
        : [],
    );
    setStatusLoading(false);
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  useEffect(() => {
    loadFeedbackData(selectedChildId);
  }, [loadFeedbackData, selectedChildId]);

  const handleChildChange = (event) => {
    const nextChildId = event.target.value;
    setSelectedChildId(nextChildId);
    if (nextChildId) {
      localStorage.setItem(SELECTED_PARENT_CHILD_STORAGE_KEY, nextChildId);
    } else {
      localStorage.removeItem(SELECTED_PARENT_CHILD_STORAGE_KEY);
    }
  };

  const handleFeedbackComplete = () => {
    loadFeedbackData(selectedChildId);
  };

  return (
    <LoggedInLayout searchBar={false}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 3 },
          py: { xs: 2, sm: 4 },
          backgroundColor: "#F6F7FB",
          minHeight: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 980,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <Box sx={cardSx}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <AssignmentTurnedInRoundedIcon sx={{ color: "#F6AA48", fontSize: 34 }} />
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#292D5B" }}>
                  Assessment Feedback
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#697089", mt: 0.5 }}>
                  Select a linked student and complete the Parent/Teacher feedback.
                </Typography>
              </Box>
            </Box>

            {linksLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress sx={{ color: "#F6AA48" }} />
              </Box>
            ) : children.length === 0 ? (
              <Box
                sx={{
                  backgroundColor: "#FFF7EB",
                  border: "1px solid rgba(246, 170, 72, 0.35)",
                  borderRadius: "12px",
                  p: 2,
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <FamilyRestroomRoundedIcon sx={{ color: "#F6AA48" }} />
                  <Typography sx={{ fontSize: 14, color: "#292D5B", fontWeight: 700 }}>
                    No linked students found.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  onClick={() => navigate("/parent-portal/profile")}
                  sx={{
                    mt: 2,
                    backgroundColor: "#292D5B",
                    borderRadius: "8px",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#292D5B" },
                  }}
                >
                  Link Student
                </Button>
              </Box>
            ) : (
              <FormControl
                size="small"
                sx={{
                  minWidth: { xs: "100%", sm: 280 },
                  maxWidth: { xs: "100%", sm: 380 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "#F7F8FB",
                    fontWeight: 700,
                    color: "#292D5B",
                  },
                }}
              >
                <Select value={selectedChildId} onChange={handleChildChange}>
                  {children.map((child) => (
                    <MenuItem key={child.id} value={String(child.id)}>
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                          {child.name || "Student"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#7B8498" }}>
                          {child.email}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {selectedChild && (
            <Box sx={cardSx}>
              {statusLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress sx={{ color: "#F6AA48" }} />
                </Box>
              ) : feedbackStatus?.parent_feedback_taken ? (
                <Box
                  sx={{
                    backgroundColor: "#F0FAF3",
                    border: "1px solid rgba(46, 125, 50, 0.18)",
                    borderRadius: "12px",
                    p: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <CheckCircleRoundedIcon sx={{ color: "#2E7D32", fontSize: 32 }} />
                    <Box>
                      <Typography sx={{ color: "#1E5B27", fontWeight: 800 }}>
                        Feedback submitted for {selectedChild.name || "this student"}
                      </Typography>
                      <Typography sx={{ color: "#5F7666", fontSize: 13, mt: 0.4 }}>
                        The student can now generate the career report from their assessment page.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : feedbackStatus?.request_pending ? (
                <ParentFeedback
                  targetUserId={selectedChildId}
                  targetStudentName={selectedChild.name}
                  onComplete={handleFeedbackComplete}
                  onBack={() => navigate("/parent-portal/dashboard")}
                  submitButtonLabel="Submit Feedback"
                  introText={`Please complete this feedback for ${selectedChild.name || "your child"} so their career report can be generated.`}
                />
              ) : (
                <Box
                  sx={{
                    backgroundColor: "#FFF7EB",
                    border: "1px solid rgba(246, 170, 72, 0.3)",
                    borderRadius: "12px",
                    p: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <HourglassEmptyRoundedIcon sx={{ color: "#F6AA48", fontSize: 32 }} />
                    <Box>
                      <Typography sx={{ color: "#292D5B", fontWeight: 800 }}>
                        No current feedback request
                      </Typography>
                      <Typography sx={{ color: "#697089", fontSize: 13, mt: 0.4 }}>
                        {selectedChild.name || "The student"} can send this from the final assessment step.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {selectedChild && (
            <Box sx={cardSx}>
              <ParentFeedbackHistory
                entries={feedbackHistory}
                loading={historyLoading}
              />
            </Box>
          )}
        </Box>
      </Box>
    </LoggedInLayout>
  );
};

export default ParentAssessmentPage;
